import { useState } from 'react';
import { Alert, Flex, Pagination, Table, Tag, Typography } from 'antd';
import type { TableColumnsType } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { GetProcessRollbackHistory } from '../../helper/apiHelper';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import type {
  ProcessRollbackHistoryEntry,
  StepName,
} from '../../Types/interfaces';
import {
  ORGANIZATION_STEPS,
  UNIVERSITY_STEPS,
  type ProcessStepDefinition,
} from './processStepDefinitions';

interface RollbackHistoryTableProps {
  processId: number;
}

// a page has this many rollbacks (one rollback can have several rows)
const DEFAULT_PAGE_SIZE = 5;
const PAGE_SIZE_OPTIONS: number[] = [5, 10, 15, 20, 25, 50, 100, 200];

interface HistoryRow extends ProcessRollbackHistoryEntry {
  // the steps of one rollback (e.g. a reset signing chain) have the same time, they are shown as
  // one group: the first row of the group spans the date, reason and person cells over the others
  groupSize: number;
}

const findDefinition = (
  stepName: StepName
): ProcessStepDefinition | undefined =>
  [...ORGANIZATION_STEPS, ...UNIVERSITY_STEPS].find(
    (definition: ProcessStepDefinition): boolean =>
      definition.stepName === stepName
  );

const isOrganizationStep = (stepName: StepName): boolean =>
  ORGANIZATION_STEPS.some(
    (definition: ProcessStepDefinition): boolean =>
      definition.stepName === stepName
  );

// the backend returns the entries the newest first, the entries of one rollback are next to each other
// and have the same time: they are one group, a page never splits a group
const groupByRollback = (
  entries: ProcessRollbackHistoryEntry[]
): ProcessRollbackHistoryEntry[][] => {
  const groups: ProcessRollbackHistoryEntry[][] = [];
  entries.forEach((entry: ProcessRollbackHistoryEntry, index: number): void => {
    if (
      index === 0 ||
      entries[index - 1].rolledBackAtUtc !== entry.rolledBackAtUtc
    ) {
      groups.push([]);
    }
    groups[groups.length - 1].push(entry);
  });
  return groups;
};

const toHistoryRows = (entries: ProcessRollbackHistoryEntry[]): HistoryRow[] =>
  entries.map(
    (entry: ProcessRollbackHistoryEntry, index: number): HistoryRow => {
      const isFirstOfGroup =
        index === 0 ||
        entries[index - 1].rolledBackAtUtc !== entry.rolledBackAtUtc;
      let groupSize = 0;
      if (isFirstOfGroup) {
        groupSize = entries.filter(
          (other: ProcessRollbackHistoryEntry): boolean =>
            other.rolledBackAtUtc === entry.rolledBackAtUtc
        ).length;
      }
      return { ...entry, groupSize: groupSize };
    }
  );

export const RollbackHistoryTable = ({
  processId,
}: RollbackHistoryTableProps) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const {
    data: entries = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: [...QUERY_KEYS.PROCESS_ROLLBACK_HISTORY, processId],
    queryFn: (): Promise<ProcessRollbackHistoryEntry[]> =>
      GetProcessRollbackHistory(processId),
  });

  const groupedCell = (row: HistoryRow) => ({ rowSpan: row.groupSize });

  const rollbackGroups = groupByRollback(entries);
  // after a new rollback the page may not exist any more, then the last page is shown
  const pageCount = Math.max(1, Math.ceil(rollbackGroups.length / pageSize));
  const shownPage = Math.min(currentPage, pageCount);
  const pageEntries = rollbackGroups
    .slice((shownPage - 1) * pageSize, shownPage * pageSize)
    .flat();

  const columns: TableColumnsType<HistoryRow> = [
    {
      title: t('processRollback.historyDate'),
      dataIndex: 'rolledBackAtUtc',
      onCell: groupedCell,
      render: (rolledBackAtUtc: string): string =>
        // the UTC time is shown in the browser's time zone
        format(new Date(rolledBackAtUtc), 'dd.MM.yyyy HH:mm'),
    },
    {
      title: t('processRollback.historyStep'),
      dataIndex: 'stepName',
      render: (stepName: StepName): string => {
        const definition = findDefinition(stepName);
        return definition ? t(definition.titleKey) : stepName;
      },
    },
    {
      title: t('processRollback.historyProcess'),
      dataIndex: 'stepName',
      key: 'process',
      render: (stepName: StepName) =>
        isOrganizationStep(stepName) ? (
          <Tag color="blue">{t('internshipProcessComp.orgProcesses')}</Tag>
        ) : (
          <Tag color="purple">
            {t('internshipProcessComp.universityProcesses')}
          </Tag>
        ),
    },
    {
      title: t('processRollback.historyReason'),
      dataIndex: 'reason',
      onCell: groupedCell,
    },
    {
      title: t('processRollback.historyBy'),
      dataIndex: 'rolledBackByName',
      onCell: groupedCell,
    },
  ];

  return (
    <Flex vertical gap={12} style={{ marginTop: 24 }}>
      <Typography.Title level={5} style={{ margin: 0 }}>
        {t('processRollback.historyTitle')}
      </Typography.Title>
      {isError ? (
        <Alert
          type="error"
          showIcon
          message={t('processRollback.historyError')}
        />
      ) : (
        <>
          <Table<HistoryRow>
            rowKey="id"
            size="small"
            bordered
            loading={isLoading}
            columns={columns}
            dataSource={toHistoryRows(pageEntries)}
            // paged by rollback below, the table's own pagination would split the groups
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: t('processRollback.noHistory') }}
          />
          {/* hidden only when the rollbacks fit on a page even at the smallest size, otherwise the
              size selector would disappear after choosing a bigger size */}
          {rollbackGroups.length > PAGE_SIZE_OPTIONS[0] && (
            <Pagination
              align="end"
              size="small"
              current={shownPage}
              pageSize={pageSize}
              total={rollbackGroups.length}
              showSizeChanger
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              showTotal={(total: number): string =>
                t('processRollback.historyTotal', { count: total })
              }
              onChange={(page: number, newPageSize: number): void => {
                setCurrentPage(newPageSize === pageSize ? page : 1);
                setPageSize(newPageSize);
              }}
            />
          )}
        </>
      )}
    </Flex>
  );
};
